import type {
  AgentDetailResponse,
  AgentListResponse,
  AgentRecord,
  AgentRunDetailResponse,
  AgentRunListResponse,
  AgentRunRecord,
  AgentType,
} from '@/services/studio/types';
import {
  asRecord,
  numberOrNull,
  numberValue,
  recordValue,
  stringOrNull,
  stringValue,
} from '@/services/studio/primitives';
import { normalizeRunStepRecord, normalizeArtifactRecord } from '@/services/migrations/normalize';

export function normalizeAgentListResponse(payload: unknown): AgentListResponse {
  const record = asRecord(payload);
  const agentSource =
    Array.isArray(payload)
      ? payload
      : Array.isArray(record?.agents)
        ? record.agents
        : Array.isArray(asRecord(record?.data)?.agents)
          ? asRecord(record?.data)?.agents
          : [];
  const agents = Array.isArray(agentSource) ? agentSource : [];

  return {
    agents: agents.map(normalizeAgentRecord),
  };
}

export function normalizeAgentDetailResponse(payload: unknown): AgentDetailResponse {
  const record = asRecord(payload);
  const data = asRecord(record?.data);
  const agentSource = record?.agent ?? data?.agent ?? data ?? payload;
  const runsSource = record?.runs ?? record?.agent_runs ?? data?.runs ?? data?.agent_runs;

  return {
    agent: normalizeAgentRecord(agentSource),
    runs: Array.isArray(runsSource) ? runsSource.map((run) => normalizeAgentRunRecord(run, { includeResult: false })) : [],
  };
}

export function normalizeAgentRunDetailResponse(payload: unknown): AgentRunDetailResponse {
  const record = asRecord(payload);
  const data = asRecord(record?.data);
  const runSource = record?.run ?? data?.run ?? data ?? payload;
  const artifactsSource = record?.artifacts ?? data?.artifacts;
  const stepsSource = record?.steps ?? data?.steps;
  return {
    run: normalizeAgentRunRecord(runSource, { includeResult: true }),
    steps: Array.isArray(stepsSource) ? stepsSource.map(normalizeRunStepRecord) : [],
    artifacts: Array.isArray(artifactsSource)
      ? artifactsSource.map(normalizeArtifactRecord)
      : [],
  };
}

export function normalizeAgentRunListResponse(payload: unknown): AgentRunListResponse {
  const record = asRecord(payload);
  const data = asRecord(record?.data);
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(record?.runs)
      ? record.runs
      : Array.isArray(data?.runs)
        ? data.runs
        : [];
  return {
    runs: source.map((run) => normalizeAgentRunRecord(run, { includeResult: false })),
  };
}

export function normalizeAgentRunRecord(
  input: unknown,
  opts: { includeResult?: boolean } = {}
): AgentRunRecord {
  const raw = asRecord(input) ?? {};
  return {
    id: stringValue(raw.id ?? raw.run_id, "run"),
    agent_id: stringValue(raw.agent_id ?? raw.agent_slug ?? raw.slug, ""),
    org_id: stringValue(raw.org_id, ""),
    user_id: stringOrNull(raw.user_id),
    surface_name: stringValue(raw.surface_name ?? raw.agent_name ?? raw.name, ""),
    surface_slug: stringValue(raw.surface_slug ?? raw.agent_slug ?? raw.slug, ""),
    surface_type: stringValue(raw.surface_type ?? raw.agent_type, ""),
    target_name: stringOrNull(raw.target_name ?? raw.metadata_name),
    target_type: stringOrNull(raw.target_type ?? raw.metadata_type),
    target_id: stringOrNull(raw.target_id ?? raw.metadata_id),
    input: stringValue(raw.input ?? raw.request, ""),
    status: stringValue(raw.status, "done") as AgentRunRecord["status"],
    result_md: opts.includeResult ? stringOrNull(raw.result_md ?? raw.markdown ?? raw.final_output) : null,
    error_message: stringOrNull(raw.error_message ?? raw.error),
    tool_calls: Array.isArray(raw.tool_calls ?? raw.steps)
      ? ((raw.tool_calls ?? raw.steps) as Array<Record<string, unknown>>)
      : [],
    started_at: stringValue(raw.started_at ?? raw.created_at, new Date().toISOString()),
    completed_at: stringOrNull(raw.completed_at),
    total_ms: numberOrNull(raw.total_ms),
    source: stringValue(raw.source, "manual"),
  };
}

export function normalizeAgentRecord(input: unknown): AgentRecord {
  const raw = asRecord(input) ?? {};
  const slug = stringValue(raw.slug, "");
  const id = slug || stringValue(raw.id ?? raw.uuid ?? raw.agent_id ?? raw.name, "agent");
  const agentType = normalizeAgentType(raw.agent_type ?? raw.type ?? raw.agent_type_code);
  const metadata = asRecord(raw.metadata) ?? {};
  const rawTools = Array.isArray(raw.tools)
    ? raw.tools
    : Array.isArray(raw.capabilities)
      ? raw.capabilities.map((capability) => {
          const item = asRecord(capability);
          return item?.name ?? item?.code ?? item?.tool_name ?? capability;
        })
      : [];

  const description = stringValue(raw.description ?? metadata.description, "Agent ready to run against the selected org.");
  const goal = stringValue(raw.goal ?? metadata.goal ?? description, description);

  return {
    id,
    org_id: stringValue(raw.org_id ?? metadata.org_id, ""),
    user_id: stringOrNull(raw.user_id ?? metadata.user_id),
    name: stringValue(raw.name ?? raw.display_name, "Untitled agent"),
    slug: slug || id,
    agent_type: agentType,
    description,
    goal,
    prompt: stringValue(raw.prompt ?? asRecord(raw.active_prompt)?.prompt, ""),
    prompt_version: numberValue(raw.prompt_version ?? asRecord(raw.active_prompt)?.version, 1),
    config: recordValue(raw.config),
    llm_config: recordValue(raw.llm_config ?? raw.llm_model),
    capabilities: Array.isArray(raw.capabilities) ? (raw.capabilities as Array<Record<string, unknown>>) : [],
    scope: recordValue(raw.scope ?? metadata.scope),
    tools: rawTools.map((tool) => stringValue(tool, "")).filter(Boolean),
    plan: Array.isArray(raw.plan) ? (raw.plan as AgentRecord["plan"]) : [],
    safety_level: stringValue(raw.safety_level ?? metadata.safety_level, agentType === "actionable" ? "approval_required" : "read_only"),
    is_default: Boolean(raw.is_default ?? metadata.is_default ?? raw.category === "default"),
    success_count: numberValue(raw.success_count, 0),
    error_count: numberValue(raw.error_count, 0),
    last_activity_at: stringOrNull(raw.last_activity_at),
    created_at: stringValue(raw.created_at ?? raw.inserted_at, ""),
    updated_at: stringValue(raw.updated_at, ""),
  };
}

export function normalizeAgentType(value: unknown): AgentType {
  const raw = typeof value === "object" && value !== null
    ? stringValue((value as Record<string, unknown>).code ?? (value as Record<string, unknown>).name, "")
    : stringValue(value, "");
  const lower = raw.toLowerCase();
  if (lower.includes("action") || lower.includes("orchestration") || lower.includes("workflow")) return "actionable";
  return "queryable";
}